import { impressionConfig } from "./config";

/**
 * Persisted to sessionStorage so a reload continues the same session_id, client_seq counter, and
 * first_impression bookkeeping. Restarting the sequence would reuse client_seq (rejected by the
 * backend); forgetting `seen` would re-flag already-seen posts. Per-tab — two tabs are two
 * independent sessions (sessions AC-W3).
 */
interface SessionState {
  id: string; // client UUID
  lastActivityAt: number; // last event stamp; a gap > sessionIdleMs starts a new session
  seq: number; // next client_seq (0-based, +1 per event, never reused)
  seen: string[]; // statusIds seen this session ("seen once = seen")
}

let cachedSession: SessionState | undefined;

const readSession = (): SessionState | undefined => {
  if (cachedSession === undefined) {
    try {
      const storedSession = sessionStorage.getItem(
        impressionConfig.sessionStorageKey,
      );
      if (storedSession) {
        cachedSession = JSON.parse(storedSession) as SessionState;
      }
    } catch {
      /* storage disabled */
    }
  }
  return cachedSession;
};

const writeSession = (state: SessionState): void => {
  cachedSession = state;
  try {
    sessionStorage.setItem(
      impressionConfig.sessionStorageKey,
      JSON.stringify(state),
    );
  } catch {
    /* storage disabled — `cachedSession` still serves this tab */
  }
};

const getCurrentSessionId = (): string | undefined => readSession()?.id;

/** Test seam: seed session state in memory and sessionStorage. */
const __seedSession = (sessionId: string): void => {
  writeSession({
    id: sessionId,
    lastActivityAt: Date.now(),
    seq: 1,
    seen: [],
  });
};

/** Test seam: clear in-memory cache and persisted session state. */
const __resetSession = (): void => {
  cachedSession = undefined;

  try {
    sessionStorage.removeItem(impressionConfig.sessionStorageKey);
  } catch {
    /* storage disabled */
  }
};

export {
  __resetSession,
  __seedSession,
  getCurrentSessionId,
  readSession,
  writeSession,
};

export type { SessionState };
